const DEFAULT_ALLOWED_TAGS = new Set(['b', 'strong', 'i', 'em', 'u', 'span', 'p', 'br', 'div', 'a']);

const COLOR_CLASS_MAP: Record<string, string> = {
  '#000000': 'rt-color-black',
  '#333333': 'rt-color-gray-800',
  '#666666': 'rt-color-gray-600',
  '#999999': 'rt-color-gray-500',
  '#ffffff': 'rt-color-white',
  '#ff0000': 'rt-color-red',
  '#00ff00': 'rt-color-green',
  '#0000ff': 'rt-color-blue',
  '#ffff00': 'rt-color-yellow',
  '#ff00ff': 'rt-color-magenta',
  '#00ffff': 'rt-color-cyan',
  '#ffa500': 'rt-color-orange',
  '#800080': 'rt-color-purple',
  '#ffc0cb': 'rt-color-pink',
  '#a52a2a': 'rt-color-brown',
  '#008000': 'rt-color-dark-green',
  '#000080': 'rt-color-dark-blue',
};

const ALLOWED_RICH_TEXT_CLASSES = new Set([
  ...Object.values(COLOR_CLASS_MAP),
  'rt-underline',
  'rt-link',
  'btn',
  'btn-sm',
  'btn-outline-primary',
  'ms-1',
  'me-1',
  'ql-align-center',
  'ql-align-right',
  'ql-align-justify',
]);

const DEFAULT_LINK_CLASS = 'btn btn-sm btn-outline-primary ms-1 me-1 rt-link';

const normalizeColorValue = (raw: string): string => raw.trim().toLowerCase().replace(/\s+/g, '');

const parseRgbColor = (raw: string): string | null => {
  const rgbMatch = raw.match(/^rgba?\((\d{1,3}),(\d{1,3}),(\d{1,3})(?:,[^)]+)?\)$/i);
  if (!rgbMatch) return null;
  const r = Math.max(0, Math.min(255, Number(rgbMatch[1])));
  const g = Math.max(0, Math.min(255, Number(rgbMatch[2])));
  const b = Math.max(0, Math.min(255, Number(rgbMatch[3])));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};

const normalizeToHex = (raw: string): string | null => {
  const value = normalizeColorValue(raw);
  if (!value) return null;
  if (value.startsWith('#')) {
    if (/^#[0-9a-f]{3}$/i.test(value)) {
      return `#${value.slice(1).split('').map((ch) => `${ch}${ch}`).join('')}`;
    }
    if (/^#[0-9a-f]{6}$/i.test(value)) return value;
    return null;
  }
  return parseRgbColor(value);
};

const colorToClass = (raw: string): string | null => {
  const normalized = normalizeToHex(raw);
  if (!normalized) return null;
  return COLOR_CLASS_MAP[normalized] || null;
};

const applySafeClasses = (element: Element, classes: Iterable<string>) => {
  const safe = Array.from(classes).filter((name) => ALLOWED_RICH_TEXT_CLASSES.has(name));
  if (safe.length > 0) {
    element.setAttribute('class', safe.join(' '));
  } else {
    element.removeAttribute('class');
  }
};

const unwrapElement = (element: Element) => {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

export interface RichTextSanitizeOptions {
  isAllowedHref?: (href: string) => boolean;
  linkClassName?: string;
  allowedTags?: string[];
  allowImageSrc?: (src: string) => boolean;
}

const defaultIsAllowedHref = (href: string): boolean => {
  const normalized = href.trim().toLowerCase();
  return (
    normalized.startsWith('/') ||
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('mailto:')
  );
};

const sanitizeAnchor = (element: HTMLElement, options: RichTextSanitizeOptions) => {
  const href = (element.getAttribute('href') || '').trim();
  const isAllowedHref = options.isAllowedHref || defaultIsAllowedHref;
  if (!href || !isAllowedHref(href)) {
    unwrapElement(element);
    return;
  }
  element.setAttribute('href', href);
  element.setAttribute('target', '_blank');
  element.setAttribute('rel', 'noopener noreferrer');
  applySafeClasses(element, (options.linkClassName || DEFAULT_LINK_CLASS).split(' '));
};

const defaultAllowImageSrc = (src: string): boolean => {
  const normalized = src.trim().toLowerCase();
  return (
    normalized.startsWith('/') ||
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('data:')
  );
};

const sanitizeImage = (element: HTMLElement, options: RichTextSanitizeOptions) => {
  const src = (element.getAttribute('src') || '').trim();
  const allowImageSrc = options.allowImageSrc || defaultAllowImageSrc;
  if (!src || !allowImageSrc(src)) {
    element.remove();
    return;
  }
  const alt = element.getAttribute('alt');
  Array.from(element.attributes).forEach((attr) => {
    const attrName = attr.name.toLowerCase();
    if (attrName === 'src' || attrName === 'alt') return;
    element.removeAttribute(attr.name);
  });
  element.setAttribute('src', src);
  if (alt) element.setAttribute('alt', alt);
  else element.removeAttribute('alt');
  element.removeAttribute('class');
};

const sanitizeElement = (element: HTMLElement, options: RichTextSanitizeOptions, allowedTags: Set<string>) => {
  const tag = element.tagName.toLowerCase();

  if (tag === 'script' || tag === 'style' || tag === 'iframe' || tag === 'object') {
    element.remove();
    return;
  }

  if (tag === 'font') {
    const replacement = document.createElement('span');
    replacement.innerHTML = element.innerHTML;
    const fontColor = element.getAttribute('color');
    if (fontColor) {
      const colorClass = colorToClass(fontColor);
      if (colorClass) replacement.classList.add(colorClass);
    }
    element.replaceWith(replacement);
    sanitizeElement(replacement, options, allowedTags);
    return;
  }

  if (!allowedTags.has(tag)) {
    unwrapElement(element);
    return;
  }

  if (tag === 'img') {
    sanitizeImage(element, options);
    return;
  }

  const style = element.getAttribute('style') || '';
  const existingClassList = (element.getAttribute('class') || '').split(/\s+/).filter(Boolean);
  const safeClasses = new Set(existingClassList.filter((name) => ALLOWED_RICH_TEXT_CLASSES.has(name)));

  if (style) {
    style.split(';').forEach((rule) => {
      const [propRaw, valueRaw = ''] = rule.split(':');
      const prop = (propRaw || '').trim().toLowerCase();
      const value = (valueRaw || '').trim();
      if (!prop || !value) return;
      if (prop === 'color' || prop === 'background-color') {
        const colorClass = colorToClass(value);
        if (colorClass) safeClasses.add(colorClass);
      }
      if (prop === 'text-decoration' && value.toLowerCase().includes('underline')) {
        safeClasses.add('rt-underline');
      }
    });
  }

  Array.from(element.attributes).forEach((attr) => {
    const attrName = attr.name.toLowerCase();
    if (attrName.startsWith('on') || attrName === 'style') {
      element.removeAttribute(attr.name);
    }
  });

  if (tag === 'a') {
    sanitizeAnchor(element, options);
    return;
  }

  applySafeClasses(element, safeClasses);
};

const walkAndSanitize = (root: HTMLElement, options: RichTextSanitizeOptions) => {
  const allowedTags = options.allowedTags?.length
    ? new Set(options.allowedTags.map((tag) => tag.toLowerCase()))
    : DEFAULT_ALLOWED_TAGS;
  const elements = Array.from(root.querySelectorAll('*')) as HTMLElement[];
  elements.forEach((el) => sanitizeElement(el, options, allowedTags));
};

export const sanitizeRichTextHtml = (html: string, options: RichTextSanitizeOptions = {}): string => {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/on\w+='[^']*'/gi, '')
      .replace(/style="[^"]*"/gi, '')
      .replace(/style='[^']*'/gi, '');
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  walkAndSanitize(container, options);
  return container.innerHTML;
};

export const linkifyRichTextHtml = (html: string, options: RichTextSanitizeOptions = {}): string => {
  if (!html || typeof document === 'undefined') return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    if (!textNode.parentElement || textNode.parentElement.tagName.toLowerCase() !== 'a') {
      textNodes.push(textNode);
    }
    current = walker.nextNode();
  }

  const isAllowedHref = options.isAllowedHref || defaultIsAllowedHref;
  const linkClasses = (options.linkClassName || DEFAULT_LINK_CLASS).split(' ');

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    if (!urlRegex.test(text)) {
      urlRegex.lastIndex = 0;
      return;
    }
    urlRegex.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = urlRegex.exec(text)) !== null) {
      const raw = match[0];
      const start = match.index;
      if (start > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      }
      const href = raw.startsWith('www.') ? `https://${raw}` : raw;
      if (isAllowedHref(href)) {
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.textContent = raw;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        applySafeClasses(anchor, linkClasses);
        fragment.appendChild(anchor);
      } else {
        fragment.appendChild(document.createTextNode(raw));
      }
      lastIndex = start + raw.length;
    }
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    node.parentNode?.replaceChild(fragment, node);
  });
  return container.innerHTML;
};

export const applyColorClassToSelection = (editor: HTMLElement, colorHex: string): void => {
  const colorClass = colorToClass(colorHex);
  if (!colorClass) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  const anchor = selection.anchorNode;
  if (!anchor || !editor.contains(anchor)) return;
  if (range.collapsed) return;

  const span = document.createElement('span');
  span.classList.add(colorClass);
  const extracted = range.extractContents();
  span.appendChild(extracted);
  range.insertNode(span);

  const newRange = document.createRange();
  newRange.selectNodeContents(span);
  selection.removeAllRanges();
  selection.addRange(newRange);
};
